import React, { useState } from 'react';
import * as Select from '@radix-ui/react-select'; // Import Radix Select
import { PlaygroundPosition } from '../../pages/PlaygroundPage/PlaygroundPage';
import './PlaygroundSettingsBar.css';

interface SeatMeta { id: number; label: string; }
// Use Position type consistent with PlaygroundPage
type Position = 'BTN' | 'SB' | 'BB' | 'UTG' | 'UTG+1' | 'UTG+2' | 'MP' | 'LJ' | 'CO';

interface PlaygroundSettingsBarProps {
  seats: SeatMeta[]; // Still needed to populate options?
  // --- Use Position Props --- 
  selectedHeroPosition: PlaygroundPosition | null;
  onSetHeroPosition: (position: PlaygroundPosition | null) => void;
  // --- Add SB/BB Props --- 
  smallBlind: number;
  bigBlind: number;
  onSmallBlindChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBigBlindChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  // REMOVED: sbSeatId, bbSeatId props and setters
  // heroSeatId: number | null;
  // sbSeatId: number | null;
  // bbSeatId: number | null;
  // onSetHero: (seatId: number | null) => void;
  // onSetSB: (seatId: number | null) => void;
  // onSetBB: (seatId: number | null) => void;
  isSetupComplete: boolean;
  onSetupStatusChange: (isCompleted: boolean) => void; // ADDED: Callback for setup status changes
}

type ActiveTab = 'Setup' | 'Actions' | 'History';

const PlaygroundSettingsBar: React.FC<PlaygroundSettingsBarProps> = ({
  seats,
  // --- Use Position Props ---
  selectedHeroPosition,
  onSetHeroPosition,
  // --- Destructure SB/BB Props --- 
  smallBlind,
  bigBlind,
  onSmallBlindChange,
  onBigBlindChange,
  isSetupComplete,
  onSetupStatusChange // ADDED: Destructure new prop
}) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('Setup');

  // Radix onValueChange provides the value directly
  const handlePositionSelectChange = (value: string) => {
    const positionValue = value as Position | "";
    onSetHeroPosition(positionValue === "" ? null : positionValue);
    // Notify parent about setup status change based on hero position selection
    onSetupStatusChange(positionValue !== ""); 
  };

  const renderSetupControls = () => (
    <div className="setup-controls">
      <div className="setup-item">
        <label htmlFor="settings-hero-position-select" className="radix-select-label">Hero Position:</label>
        <Select.Root 
          value={selectedHeroPosition ?? ''} 
          onValueChange={handlePositionSelectChange}
        >
          <Select.Trigger className="radix-select-trigger" aria-label="Hero Position">
            <Select.Value placeholder="-- Select --" />
          </Select.Trigger>
          <Select.Portal>
            <Select.Content 
              className="radix-select-content" 
              position="popper"
              side="bottom" 
              align="start"
              sideOffset={5}
            >
              <Select.Viewport className="radix-select-viewport">
                 {/* REMOVED: Item with value="" */}
                 {/* <Select.Item value="" className="radix-select-item disabled-item">-- Select --</Select.Item> */}
                 {/* Map positions from seats array labels */}
                 {Array.from(new Set(seats.map(seat => seat.label as Position))).map(pos => (
                  <Select.Item key={pos} value={pos} className="radix-select-item">
                    <Select.ItemText>{pos}</Select.ItemText>
                  </Select.Item>
                 ))}
               </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </div>
      {/* --- Add SB/BB Inputs --- */}
      <div className="setup-item">
        <label htmlFor="settings-sb-input">SB:</label>
        <input 
          type="number" 
          id="settings-sb-input"
          className="number-input" // Add class for styling
          value={smallBlind} 
          onChange={onSmallBlindChange} 
          min="0" // Prevent negative blinds
          onFocus={(e) => e.target.select()} // Select text on focus
        />
      </div>
      <div className="setup-item">
        <label htmlFor="settings-bb-input">BB:</label>
        <input 
          type="number" 
          id="settings-bb-input"
          className="number-input" // Add class for styling
          value={bigBlind} 
          onChange={onBigBlindChange} 
          min="0" // Prevent negative blinds
          onFocus={(e) => e.target.select()} // Select text on focus
        />
      </div>
      {/* REMOVED SB/BB Selectors */}
      {/* REMOVED Confirm button */}
      {/* 
      <button 
        className="confirm-button"
        onClick={onConfirmSetup}
        disabled={!canConfirmSetup} // Only disable if no position selected
      >
        Confirm Setup
      </button>
      */}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Setup':
        return renderSetupControls();
      case 'Actions':
        return <div>Actions Content...</div>;
      case 'History':
        return <div>Game History Content...</div>;
      default:
        return null;
    }
  };

  return (
    <div className="playground-settings-bar">
       <div className="settings-tabs">
         {(['Setup', 'Actions', 'History'] as ActiveTab[]).map(tab => (
           <button 
             key={tab}
             onClick={() => setActiveTab(tab)}
             className={`settings-tab-button ${activeTab === tab ? 'active' : ''}`}
             disabled={!isSetupComplete && tab !== 'Setup'}
           >
             {tab}
           </button>
         ))}
       </div>
       <div className="settings-content">
         {renderTabContent()}
       </div>
     </div>
  );
};

export default PlaygroundSettingsBar; 